import React, { useContext, useEffect, useState } from 'react';
import { EventListProps } from './EventList.types';
import * as Styles from '../../directory/DirectoryServiceList/DirectoryServiceList.styles';
import Row from '../../components/Row/Row';
import Column from '../../components/Column/Column';
import FormWithLine from '../../components/FormWithLine/FormWithLine';
import Input from '../../components/Input/Input';
import HeartIcon from '../../components/icons/HeartIcon/HeartIcon';
import PinIcon from '../../components/icons/PinIcon/PinIcon';
import SearchIcon from '../../components/icons/SearchIcon/SearchIcon';
import HintText from '../../components/HintText/HintText';
import Pagination from '../../components/Pagination/Pagination';
import { useDirectoryShortListContext } from '../../contexts/DirectoryShortListProvider/DirectoryShortListProvider';
import DirectoryAddToShortList from '../../directory/DirectoryAddToShortList/DirectoryAddToShortList';
import useLocalStorage from '../../helpers/UseLocalStorage';
import DirectoryMap from '../../directory/DirectoryMap/DirectoryMap';
import { StaticMapProps } from '../../components/StaticMap/StaticMap.types';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { ThemeContext } from 'styled-components';
import { transformSnippet } from '../../directory/DirectoryService/DirectoryServiceTransform';
import RadioCheckboxInput from '../../components/RadioCheckboxInput/RadioCheckboxInput';
import Button from '../../components/Button/Button';
import { AlertBannerService } from '../../structure/PageStructures';
import FormButton from '../../components/FormButton/FormButton';
import { applyAllEventFilters } from '../utils/EventFilters';
import DropDownSelect from '../../components/DropDownSelect/DropDownSelect';
import NoResultsTemplate from '../../components/NoResultsTemplate/NoResultsTemplate';

const EventList: React.FunctionComponent<EventListProps> = ({
  eventsPath,
  shortListPath,
  events,
  search = '',
  setSearch,
  proximity = 2,
  showPostcodeSearch = true,
  postcode = '',
  setPostcode,
  totalResults = 0,
  pageNumber = 1,
  setPageNumber,
  perPage = 5,
  extractLength = 300,
  eventTypes = [],
  setEventTypes,
  audiences = [],
  setAudiences,
  selectedMonth,
  setSelectedMonth,
  selectedYear,
  setSelectedYear,
  mapCenter = '52.40410666556486,-0.7237065995821512',
  mapZoom = 9,
  isLoading = false,
  isError = false,
  hideOldEvents = true,
  noResultsContent,
}) => {
  const [accordions, setAccordions] = useLocalStorage(`${eventsPath.replace(/\//g, '')}-accordion`, []);
  const [openAll, setOpenAll] = useLocalStorage(`${eventsPath.replace(/\//g, '')}-accordion-all`, true);
  const [showMap, setShowMap] = useLocalStorage(`${eventsPath.replace(/\//g, '')}-show-map`, false);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const {
    favourites: { favourites: favourites, setFavourites: setFavourites },
    toggleFavourites: toggleFavourites,
    isFavourite: isFavourite,
  } = useDirectoryShortListContext();
  const [notServer, setNotServer] = useState(false);
  const [searchTerm, setSearchTerm] = useState(search);
  const [postcodeSearch, setPostcodeSearch] = useState(postcode);
  const themeContext = useContext(ThemeContext);
  const [filtersActive, setFiltersActive] = useState(false);

  useEffect(() => {
    setNotServer(true);
    setFiltersActive(hasActiveFilters());
  }, []);

  useEffect(() => {
    setFiltersActive(hasActiveFilters());
  }, [selectedMonth, selectedYear, eventTypes, audiences, search, postcode]);

  // Initialize accordions
  if (accordions.length === 0) {
    const tempAccordions = [];
    // Date filter
    tempAccordions.push(false);
    // Event types
    eventTypes?.forEach(() => {
      tempAccordions.push(false);
    });
    // Audiences
    audiences?.forEach(() => {
      tempAccordions.push(false);
    });
    setAccordions(tempAccordions);
  }

  const optionChecked = (e, categoryIndex: number, singleSelection: boolean, isEventType: boolean = true) => {
    const categories = isEventType ? eventTypes : audiences;
    const setCategories = isEventType ? setEventTypes : setAudiences;
    let newCategories = [...categories];

    if (singleSelection) {
      newCategories[categoryIndex].options.forEach((option) => {
        option.checked = false;
      });
    }

    newCategories[categoryIndex].options.find((option) => {
      if (option.id === e.target.value) {
        option.checked = !option.checked;
      }
    });

    setCategories(newCategories);
  };

  const clearCategory = (categoryIndex: number, isEventType: boolean = true) => {
    const categories = isEventType ? eventTypes : audiences;
    const setCategories = isEventType ? setEventTypes : setAudiences;
    let newCategories = [...categories];

    newCategories[categoryIndex].options.forEach((option) => {
      option.checked = false;
    });

    setCategories(newCategories);
  };

  const clearDateFilter = () => {
    setSelectedMonth(undefined);
    setSelectedYear(undefined);
  };

  const from = pageNumber * perPage - (perPage - 1);
  const to = from + (events?.length ? events.length - 1 : 0);

  const clearSearch = () => {
    window.location.href = eventsPath;
  };

  const toggleAccordion = (index) => {
    const updatedAccordions = [...accordions];
    updatedAccordions[index] = !updatedAccordions[index];
    setAccordions(updatedAccordions);
  };

  const toggleAll = () => {
    setOpenAll(!openAll);
    const updatedAccordions = accordions.map(() => {
      return openAll;
    });
    setAccordions(updatedAccordions);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setSearch(searchTerm);
    setPostcode(postcodeSearch);
  };

  const letters: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const labelLetters: string[] = letters.split('');

  const mapProps: StaticMapProps = {
    centre: mapCenter,
    zoom: mapZoom,
    size: '640x320',
    imageAltText: 'Event search results displayed on a map',
    mapMarkers: events
      .filter((event) => {
        return event.location?.latitude && event.location?.longitude;
      })
      .map((event, index) => {
        return {
          lat: parseFloat(event.location?.latitude || '0'),
          lng: parseFloat(event.location?.longitude || '0'),
          label: labelLetters[index],
          title: `<a href=${event.id}>${event.name}</a>`,
        };
      }),
  };

  const hasActiveFilters = () => {
    return (
      search !== '' ||
      postcode !== '' ||
      selectedMonth !== undefined ||
      selectedYear !== undefined ||
      eventTypes.some((category) => {
        return category.options.some((option) => {
          return option.checked == true;
        });
      }) ||
      audiences.some((category) => {
        return category.options.some((option) => {
          return option.checked == true;
        });
      })
    );
  };

  const formatEventDateTime = (startDateTime: string, endDateTime?: string) => {
    const startDate = new Date(startDateTime);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };

    let formatted = startDate.toLocaleDateString('en-GB', options);

    if (endDateTime) {
      const endDate = new Date(endDateTime);
      const endTime = endDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      formatted += ` - ${endTime}`;
    }

    return formatted;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear + i);

  return (
    <Styles.Container data-testid="EventList">
      <Row>
        <Column small="full" medium="full" large="full">
          <Styles.SearchHeader>
            <FormWithLine hideLine onSubmit={submitSearch}>
              <Row>
                <Column small="full" medium="one-half" large="one-third">
                  <Styles.Label htmlFor="eventSearch">What events are you looking for?</Styles.Label>
                  <HintText text="Enter a search word or phrase" />
                  <Input
                    name="eventSearch"
                    type="text"
                    defaultValue={searchTerm}
                    id="eventSearch"
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                    }}
                  />
                </Column>
                {showPostcodeSearch && (
                  <Column small="full" medium="one-half" large="one-third">
                    <Styles.Label htmlFor="postcode">Postcode (optional)</Styles.Label>
                    <HintText text={`Enter a postcode to see results within ${proximity} miles`} />
                    <Input
                      name="postcode"
                      type="text"
                      defaultValue={postcodeSearch}
                      id="postcode"
                      onChange={(e) => setPostcodeSearch(e.target.value)}
                      autocomplete="postal-code"
                    />
                  </Column>
                )}

                <Column small="full" medium="one-half" large="one-third">
                  <Styles.ButtonContainer>
                    <Styles.Button size="large" onClick={submitSearch} type="submit">
                      <Styles.ButtonText>Search</Styles.ButtonText>
                      <SearchIcon colourFill="#fff" />
                    </Styles.Button>

                    {filtersActive && (
                      <Styles.Button size="large" onClick={clearSearch} type="button" $isWarning={true}>
                        <Styles.ButtonText>Clear search</Styles.ButtonText>
                      </Styles.Button>
                    )}
                  </Styles.ButtonContainer>
                </Column>
              </Row>
            </FormWithLine>
          </Styles.SearchHeader>
        </Column>
        <Column small="full" medium="one-third" large="one-third">
          <Styles.FilterToggle type="button" onClick={(e) => setShowFilters(!showFilters)}>
            {showFilters ? `Hide Filters` : `Show Filters`}
          </Styles.FilterToggle>
          <Styles.FilterContainer $showFilters={showFilters}>
            <Row>
              <Column small="full" medium="full" large="full">
                <Styles.ResultInfo>Refine your search</Styles.ResultInfo>
              </Column>
              {notServer && (
                <>
                  <Column small="full" medium="full" large="full">
                    <Styles.AccordionControls>
                      {filtersActive ? (
                        <Styles.TextLink onClick={clearSearch} type="button">
                          <Styles.ButtonText>Clear all filters</Styles.ButtonText>
                        </Styles.TextLink>
                      ) : (
                        <div></div>
                      )}

                      <Styles.TextLink onClick={toggleAll} type="button" aria-expanded={!openAll}>
                        {openAll ? 'Open all' : 'Close all'}
                        <Styles.VisuallyHidden> sections</Styles.VisuallyHidden>
                      </Styles.TextLink>
                    </Styles.AccordionControls>
                  </Column>

                  {/* Date Filter */}
                  <Column small="full" medium="full" large="full">
                    <Styles.Fieldset>
                      <Styles.Legend onClick={(e) => toggleAccordion(0)}>
                        <Styles.LegendButton onClick={(e) => toggleAccordion(0)} type="button">
                          Select date range
                          <Styles.AccordionIcon $isOpen={accordions[0]} />
                        </Styles.LegendButton>
                      </Styles.Legend>
                      <Styles.Accordion $isOpen={accordions[0]}>
                        <Styles.ClearFilter>
                          <Styles.TextLink onClick={clearDateFilter}>Clear filter</Styles.TextLink>
                        </Styles.ClearFilter>
                        <Row>
                          <Column small="full" medium="one-half" large="one-half">
                            <DropDownSelect
                              id="event_month"
                              label="Month"
                              selected={selectedMonth?.toString() || ''}
                              onChange={(e) => setSelectedMonth(e.target.value ? parseInt(e.target.value) : undefined)}
                              options={[
                                { title: 'All months', value: '' },
                                ...monthNames.map((month, index) => ({
                                  title: month,
                                  value: (index + 1).toString(),
                                })),
                              ]}
                            />
                          </Column>
                          <Column small="full" medium="one-half" large="one-half">
                            <DropDownSelect
                              id="event_year"
                              label="Year"
                              selected={selectedYear?.toString() || ''}
                              onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : undefined)}
                              options={[
                                { title: 'All years', value: '' },
                                ...years.map((year) => ({
                                  title: year.toString(),
                                  value: year.toString(),
                                })),
                              ]}
                            />
                          </Column>
                        </Row>
                      </Styles.Accordion>
                    </Styles.Fieldset>
                  </Column>

                  {/* Event Types */}
                  {eventTypes?.map((category, categoryIndex) => (
                    <Column small="full" medium="full" large="full" key={category.label}>
                      <Styles.Fieldset>
                        <Styles.Legend>
                          <Styles.LegendButton onClick={(e) => toggleAccordion(categoryIndex + 1)} type="button">
                            {category.label}
                            <Styles.AccordionIcon $isOpen={accordions[categoryIndex + 1]} />
                          </Styles.LegendButton>
                        </Styles.Legend>
                        <Styles.Accordion $isOpen={accordions[categoryIndex + 1]}>
                          <Styles.ClearFilter>
                            <Styles.TextLink onClick={(e) => clearCategory(categoryIndex, true)}>
                              Clear filter
                            </Styles.TextLink>
                          </Styles.ClearFilter>
                          {category.options.map((taxonomy) => (
                            <Styles.Category key={taxonomy.id}>
                              <RadioCheckboxInput
                                name={taxonomy.vocabulary}
                                value={taxonomy.id}
                                label={taxonomy.name}
                                singleSelection={category.singleSelection}
                                checked={taxonomy.checked}
                                onChange={(e) => optionChecked(e, categoryIndex, category.singleSelection, true)}
                              />
                            </Styles.Category>
                          ))}
                        </Styles.Accordion>
                      </Styles.Fieldset>
                    </Column>
                  ))}

                  {/* Audiences */}
                  {audiences?.map((category, categoryIndex) => (
                    <Column small="full" medium="full" large="full" key={category.label}>
                      <Styles.Fieldset>
                        <Styles.Legend>
                          <Styles.LegendButton
                            onClick={(e) => toggleAccordion(eventTypes.length + categoryIndex + 1)}
                            type="button"
                          >
                            {category.label}
                            <Styles.AccordionIcon $isOpen={accordions[eventTypes.length + categoryIndex + 1]} />
                          </Styles.LegendButton>
                        </Styles.Legend>
                        <Styles.Accordion $isOpen={accordions[eventTypes.length + categoryIndex + 1]}>
                          <Styles.ClearFilter>
                            <Styles.TextLink onClick={(e) => clearCategory(categoryIndex, false)}>
                              Clear filter
                            </Styles.TextLink>
                          </Styles.ClearFilter>
                          {category.options.map((taxonomy) => (
                            <Styles.Category key={taxonomy.id}>
                              <RadioCheckboxInput
                                name={taxonomy.vocabulary}
                                value={taxonomy.id}
                                label={taxonomy.name}
                                singleSelection={category.singleSelection}
                                checked={taxonomy.checked}
                                onChange={(e) => optionChecked(e, categoryIndex, category.singleSelection, false)}
                              />
                            </Styles.Category>
                          ))}
                        </Styles.Accordion>
                      </Styles.Fieldset>
                    </Column>
                  ))}
                </>
              )}
            </Row>
          </Styles.FilterContainer>
        </Column>
        <Column small="full" medium="two-thirds" large="two-thirds">
          <Row>
            {isError ? (
              <Column small="full" medium="full" large="full">
                <AlertBannerService>
                  <p>Sorry, there was a problem fetching events. Please try again later.</p>
                </AlertBannerService>
              </Column>
            ) : isLoading ? (
              <Styles.LoadingContainer>
                <LoadingSpinner />
                <p>Loading</p>
              </Styles.LoadingContainer>
            ) : (
              <>
                <Column small="full" medium="full" large="full">
                  {events?.length > 0 ? (
                    <Styles.ResultInfo>
                      Showing {from} to {to} out of {totalResults.toLocaleString()}
                    </Styles.ResultInfo>
                  ) : (
                    <Styles.ResultInfo>
                      {noResultsContent ? (
                        <div dangerouslySetInnerHTML={{ __html: noResultsContent }} />
                      ) : (
                        <NoResultsTemplate contentType="events" />
                      )}
                    </Styles.ResultInfo>
                  )}
                  <Styles.FavouritesContainer>
                    {events?.length > 0 && (
                      <Styles.MapToggle type="button" size="large" onClick={(e) => setShowMap(!showMap)}>
                        {showMap ? `Hide Map` : `Show Map`}
                      </Styles.MapToggle>
                    )}

                    <Styles.Favourites href={shortListPath}>
                      <HeartIcon colourFill={themeContext.theme_vars.colours.action} /> Shortlist ({favourites.length})
                    </Styles.Favourites>
                  </Styles.FavouritesContainer>
                </Column>
                <Column small="full" medium="full" large="full">
                  <Pagination
                    currentPage={pageNumber}
                    totalResults={totalResults}
                    resultsPerPage={perPage}
                    postTo={eventsPath}
                    buttonClickOverride={setPageNumber}
                  />
                </Column>
                <Column small="full" medium="full" large="full">
                  {notServer && <>{events?.length > 0 && showMap && <DirectoryMap mapProps={mapProps} />}</>}
                </Column>
                {events.map((event, index) => {
                  const snippet = transformSnippet(event.description, extractLength);
                  return (
                    <Column small="full" medium="full" large="full" key={event.id}>
                      <Styles.ServiceContainer $resultNumber={index}>
                        <Row>
                          <Column small="full" medium="full" large="full">
                            <Styles.ServiceHeader>
                              <Styles.ServiceLink href={`${event.id}`}>{event.name}</Styles.ServiceLink>
                              {showMap && event.location?.latitude && event.location?.longitude && (
                                <Styles.MarkerContainer>
                                  <span>{labelLetters[index]}</span>
                                  <PinIcon colourFill={themeContext.theme_vars.colours.action_dark} />
                                </Styles.MarkerContainer>
                              )}
                            </Styles.ServiceHeader>
                          </Column>
                          <Column small="full" medium="full" large="full">
                            <Styles.Age>{formatEventDateTime(event.startDateTime, event.endDateTime)}</Styles.Age>
                            {event.organization && (
                              <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>
                                Organised by: <strong>{event.organization.name}</strong>
                              </p>
                            )}
                            {event.eventType && (
                              <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>
                                Event type: <strong>{event.eventType}</strong>
                              </p>
                            )}
                            {event.audience && event.audience.length > 0 && (
                              <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>
                                Audience: <strong>{event.audience.join(', ')}</strong>
                              </p>
                            )}
                          </Column>
                          <Column
                            small="full"
                            medium={event.organization?.logo ? 'two-thirds' : 'full'}
                            large={event.organization?.logo ? 'two-thirds' : 'full'}
                          >
                            <div>{snippet}</div>
                          </Column>
                          {event.organization?.logo && (
                            <Column small="full" medium="one-third" large="one-third">
                              <Styles.ServiceImageContainer>
                                <Styles.ServiceImage
                                  src={event.organization.logo}
                                  alt={event.name + ' logo'}
                                  loading="lazy"
                                />
                              </Styles.ServiceImageContainer>
                            </Column>
                          )}
                          <Column small="full" medium="full" large="full">
                            <DirectoryAddToShortList
                              id={event.id}
                              name={event.name}
                              snippet={snippet}
                              email={event.email}
                              website={event.url}
                              phone={event.contacts?.[0]?.phones?.flatMap((phone) => phone.number).join(', ')}
                              addresses={
                                event.location?.address
                                  ? [
                                      {
                                        id: event.location.address.id,
                                        address_1: event.location.address.address1,
                                        city: event.location.address.city,
                                        state_province: event.location.address.county,
                                        postal_code: event.location.address.postcode,
                                        country: event.location.address.country,
                                      },
                                    ]
                                  : []
                              }
                              fees={event.fees}
                            />
                          </Column>
                        </Row>
                      </Styles.ServiceContainer>
                    </Column>
                  );
                })}
                <Column small="full" medium="full" large="full">
                  <Pagination
                    currentPage={pageNumber}
                    totalResults={totalResults}
                    resultsPerPage={perPage}
                    postTo={eventsPath}
                    buttonClickOverride={setPageNumber}
                  />
                </Column>
              </>
            )}
          </Row>
        </Column>
      </Row>
    </Styles.Container>
  );
};

export default EventList;
